import type { AxiosError } from 'axios';
import axios from 'axios';

import type Profiler from '@rover/shared/js/ssr/Profiler';

import getStatsdTagForUrl from './getStatsdTagForUrl';

const axiosErrorHandler = (
  e?: AxiosError,
  PROFILER?: Profiler,
  PROFILER_KEYS?: Record<string, string>
): never => {
  if (e && e.config && e.config.method) {
    const isTimeout = e.code === 'ECONNABORTED';
    const apiUrl = getStatsdTagForUrl(e.config.url ?? '', 'No Match Found');
    const apiMethod = e.config.method.toUpperCase();

    if (isTimeout && PROFILER && PROFILER_KEYS) {
      PROFILER.increment({
        key: PROFILER_KEYS.API_CALL_TIMEOUT,
        tags: {
          apiMethod,
          apiUrl,
        },
      });
    }

    const statusCode = e.response?.status;
    if (statusCode && statusCode >= 500 && PROFILER && PROFILER_KEYS) {
      PROFILER.increment({
        key: PROFILER_KEYS.API_CALL_5XX,
        tags: {
          apiMethod,
          apiUrl,
          statusCode: String(statusCode),
        },
      });
    }

    // A failure with no response carries no status, so the message is the only record of
    // what was being requested. Decorate the error in place rather than substituting a
    // plain one: downstream `axios.isAxiosError` and `axios.isCancel` checks are what let
    // timeouts and cancellations be classified as NetworkErrors instead of reported.
    if (!statusCode) {
      e.message = `axiosUtils ${
        isTimeout ? 'timed out' : 'errored'
      } while requesting "${apiMethod} ${e.config.url}": ${e.message}`;
    }

    throw e;
  }

  if (axios.isAxiosError(e)) throw e;

  throw new Error(String(e));
};

export default axiosErrorHandler;
